import { encounterCreatureField } from '../../shared/array-types';
import { readInventoryField, readInventoryRows, readItemUuidSlot, readLayerListEntryUuid } from './inventory-raw-readers';
import { phpJsonTruthy } from './php-json-values';

type Row = Record<string, unknown>;
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface EquippedItemRequirementReaders {
    entries: (row: Row | null, key: string) => readonly unknown[];
    inventoryField?: (row: Row, key: string) => unknown;
    /** The compiler's selected worn-loadout reader, never aggregated inventory_equipped entries. */
    wornEquippedEntries?: (host: Row) => readonly unknown[];
    /** Static authored entries need their real catalog definition through the compiler's existing reader lane. */
    definition?: (id: string) => Row | null;
}

export class EquippedItemRequirementsUnknown extends Error {
    constructor(readonly site: string) { super(site); }
}

/** Live possession remains authoritative even when its lean inventory bucket is absent/empty. */
export function hasLiveEquipmentContext(host: Row): boolean {
    return readItemUuidSlot(encounterCreatureField(host.encounter_creatures, 'encounter_creature_id')) !== ''
        || phpJsonTruthy(host.player_characters);
}

/** Catalog identity only: an inventory instance, duplicate copy or display projection cannot supply another set piece. */
export function equippedCatalogItemIds(host: Row, readers: EquippedItemRequirementReaders): ReadonlySet<string> {
    const ids = new Set<string>();
    if (hasLiveEquipmentContext(host)) {
        const field = readers.inventoryField ?? readInventoryField;
        for (const row of readInventoryRows(host, 'inventory')) {
            if (phpJsonTruthy(field(row, 'inventory_static_display')) || readItemUuidSlot(field(row, 'equipped')) === '') continue;
            const id = readItemUuidSlot(field(row, 'inventory_item_id'));
            if (id) ids.add(id.toUpperCase());
        }
    } else {
        if (!readers.wornEquippedEntries) throw new EquippedItemRequirementsUnknown('equipment-set-worn-loadout');
        for (const entry of readers.wornEquippedEntries(host)) {
            const id = readLayerListEntryUuid(entry);
            if (uuid.test(id)) ids.add(id.toUpperCase());
        }
    }
    return ids;
}

/** One requirement rule for condition compilation and cast admission. Malformed requirements remain unmet. */
export function unmetEquippedItemRequirements(host: Row, ability: Row | null, readers: EquippedItemRequirementReaders): string[] {
    const required = readers.entries(ability, 'requires_equipped_item');
    if (!required.length) return [];
    const equipped = equippedCatalogItemIds(host, readers);
    const live = hasLiveEquipmentContext(host);
    const missing: string[] = [];
    for (const entry of required) {
        const id = readLayerListEntryUuid(entry).toUpperCase();
        if (!uuid.test(id)) missing.push(id || 'invalid-requirement-entry');
        else if (!equipped.has(id)) missing.push(id);
        else if (!live && !readers.definition?.(id)) throw new EquippedItemRequirementsUnknown('equipment-set-definition-not-held');
    }
    return missing;
}
